import styled from '@emotion/styled';
import React, { useEffect, useMemo, useState } from 'react';
import { WidgetItemLiveChannel, getCompactConditionalProp } from '../../../../apiService';
import Carousel from '../../../../Carousel';
import { Card, Text } from '../../../../styles';
import Row from '../../../../styles/Row';
import PageOverlay from '../../../../styles/PageOverlay';
import { pulse } from '../../../../styles/ShimmerAnimation';
import { SpacingProps } from '../../../../withStyleSystem';
import { useViewersOwnFlags } from '../../../../utils/useViewerAccess';
import LiveCarouselCard from './LiveCarouselCard';
import LivePromotedCard from './LivePromotedCard';

/** Below/at this count the items are shown as a promoted grid, above it as a carousel. */
const PROMOTED_COUNT = 3;

type LiveCardListProps = {
  items: WidgetItemLiveChannel[];
  title?: string;
  more?: string;
  id?: string;
  /** True when this is the first widget after the teaser, so it sits closest to it. */
  afterTeaser?: boolean;
} & SpacingProps;

export default function LiveCardList({ items, title, more, id, afterTeaser, ...spacingProps }: LiveCardListProps) {
  const liveItems = useMemo(() => items.filter((item) => item?.conductor), [items]);
  const [activeCardId, setActiveCardId] = useState<string | null>(null);

  // A widget requested anonymously carries a generic conditional_flag that isn't user-aware, so
  // playable/free/hasAccess come out wrong and the play CTA would send e.g. free channels to the
  // subscription page. Fetch the per-user flags (the same source the single-content page relies on)
  // and merge them in, so the cards route exactly like the content page's play button. Widgets
  // requested with the viewer's token already carry them.
  const ownFlags = useViewersOwnFlags();
  const [flagMap, setFlagMap] = useState<Record<string, string>>({});
  // A card is only shown once its conditional-prop response is in; until then it's a skeleton.
  const hasConditionalIds = useMemo(
    () => liveItems.some((item) => !!item.conductor.video_content?.short_id),
    [liveItems],
  );
  const [loading, setLoading] = useState(hasConditionalIds);
  useEffect(() => {
    if (ownFlags === undefined) return;
    const ids = ownFlags ? [] : liveItems
      .map((item) => item.conductor.video_content?.short_id)
      .filter((v): v is string => !!v);
    if (!ids.length) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getCompactConditionalProp(ids)
      .then((res) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        res.data?.forEach((c) => { if (c.short_id) map[c.short_id] = c.conditional_flag; });
        setFlagMap(map);
      })
      .catch(() => undefined) // keep the widget flags if the request fails
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [liveItems, ownFlags]);

  const enrichedItems = useMemo(
    () => liveItems.map((item) => {
      const vc = item.conductor.video_content;
      const flag = vc?.short_id ? flagMap[vc.short_id] : undefined;
      if (!vc || !flag) return item;
      return { ...item, conductor: { ...item.conductor, video_content: { ...vc, conditional_flag: flag } } };
    }),
    [liveItems, flagMap],
  );

  const renderPromotedCard = (item: typeof enrichedItems[number], index: number) => {
    const content = item.conductor.video_content;
    if (!content) return null;
    if (loading) return <PromotedCardSkeleton />;
    const cardId = content.id ?? content.short_id ?? `${index}`;
    return (
      <LivePromotedCard
        imagePath={content.cover_image?.path || content.poster_image?.path}
        content={content}
        conductorTitle={item.conductor.title}
        startStreamTime={item.conductor.start_stream_time}
        isLive={item.conductor.is_live}
        active={cardId === activeCardId}
        onHoverChange={setActiveCardId}
      />
    );
  };

  if (enrichedItems.length <= PROMOTED_COUNT) {
    return (
      <Root afterTeaser={afterTeaser}>
        <PageOverlay visible={!!activeCardId} />
        {title && (
          <Text variant="h2" weight="medium" mb={1}>
            {title}
          </Text>
        )}

        {/* Mobile: each promoted channel shown full size in a swipeable carousel. */}
        <MobilePromoted>
          <Carousel m={0}>
            <Row cols={{ xs: 1 }}>
              {enrichedItems.map((item, index) => (
                <PromotedSlide key={`${item.channel_id}-${index}`}>
                  {renderPromotedCard(item, index)}
                </PromotedSlide>
              ))}
            </Row>
          </Carousel>
        </MobilePromoted>

        {/* sm and up: the original promoted grid (one large + the others beside it). */}
        <DesktopPromoted>
          <PromotedContainer>
            {enrichedItems.map((item, index) => {
              const content = item.conductor.video_content;
              const cardId = content?.id ?? content?.short_id ?? `${index}`;
              const active = cardId === activeCardId;
              return (
                <PromotedCardWrapper index={index} key={`${item.channel_id}-${index}`} style={{ zIndex: active ? 9999 : 1 }}>
                  {renderPromotedCard(item, index)}
                </PromotedCardWrapper>
              );
            })}
          </PromotedContainer>
        </DesktopPromoted>
      </Root>
    );
  }

  return (
    <Root afterTeaser={afterTeaser}>
      <CustomCarousel m={0} title={title} more={more} hoverOverflowVisibleOnDesktop>
        <Row id={id} cols={{ xs: 2, sm: 4, md: 6 }} {...spacingProps}>
          {enrichedItems.map((item, index) => (
            loading
              ? <CarouselCardSkeleton key={`${item.channel_id}-${index}`} />
              : <LiveCarouselCard key={`${item.channel_id}-${index}`} item={item} />
          ))}
        </Row>
      </CustomCarousel>
    </Root>
  );
}

// The teaser reserves its true height (see TeaserVideo), so by default widgets after it sit cleanly
// below. The live widget is the exception: right after the teaser it should overlap onto the video.
// This negative top margin (desktop only) pulls it up onto the teaser; WidgetWrapper's afterTeaser
// zIndex keeps it painted above the teaser so it isn't clipped. (It collapses with WidgetWrapper's
// 4rem top margin, so the net pull-up is ~2rem; the carousel branch overlaps a little more via its
// own marginTop: -3rem hover-overflow pull.) Tune the value to taste.
const Root = styled.div<{ afterTeaser?: boolean }>(({ theme, afterTeaser }) => ({
  [theme.breakpoints.up('md')]: {
    ...(afterTeaser ? { marginTop: `-${theme.spacing(8)}` } : null),
  },
}));

// Mobile (< sm) shows the promoted channels as a full-size swipeable carousel; the grid below
// takes over from sm up.
const MobilePromoted = styled.div(({ theme }) => ({
  display: 'block',
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}));

const DesktopPromoted = styled.div(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'block',
  },
}));

const PromotedSlide = styled.div(() => ({
  position: 'relative',
}));

const PromotedContainer = styled.div(({ theme }) => ({
  display: 'flex',
  marginLeft: `-${theme.spacing(2)}`,
}));

const PromotedCardWrapper = styled.div<{ index: number }>(({ theme, index }) => ({
  gridArea: `item${index}`,
  position: 'relative',
  [theme.breakpoints.up('sm')]: {
    width: `${100 / 3}%`,
    marginLeft: theme.spacing(2),
  },
}));

const CustomCarousel = styled(Carousel)(() => ({
  zIndex: 50,
}));

// ---------- //
// SKELETONS  //
// ---------- //

// Reuses the real Card shape (horizontal 56% ratio, background[500]) so the skeleton matches the
// card's exact size; just adds the shared pulse animation.
const ShimmerCard = styled(Card)({
  animation: `${pulse} 1s linear infinite`,
});

const SkeletonTitle = styled.div(({ theme }) => ({
  marginTop: '0.5rem',
  marginBottom: '0.5rem',
  height: '0.9rem',
  width: '70%',
  borderRadius: theme.radius.medium,
  backgroundColor: theme.palette.background[500][0],
  animation: `${pulse} 1s linear infinite`,
}));

const PromotedCardSkeleton = () => <ShimmerCard orientation="horizontal" block />;

// The carousel card also shows a title beneath it, so the skeleton mirrors that.
const CarouselCardSkeleton = () => (
  <div>
    <ShimmerCard orientation="horizontal" block />
    <SkeletonTitle />
  </div>
);
